import React, { useMemo, useState } from "react";
import { XCircle, FileText, Shield, MapPin, Image as ImageIcon, Package, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const IMAGE_BASE = (process.env.REACT_APP_API_URL || "/api")
  .replace(/\/+$/, "")
  .replace(/\/api$/i, "");

const toNumberId = (p) => Number(p?.productId ?? p?.id);

const CompanyDetailsModal = ({ company, onClose, onUpdate, onDelete, isAdmin = false, allProducts = [], loading }) => {
  const { t } = useTranslation();
  const storageKey = `company_licenses_${company.id}`;

  const buildInitialState = () => {
    const baseState = {
      hasPaid: !!company.hasPaid,
      productAccess: (company.products || []).reduce((acc, p) => {
        const id = Number(p.productId);
        acc[id] = {
          hasPaid: !!p.hasPaid,
          hasAccess: !!p.hasAccess,
          startDate: p.licenseStartDate ? String(p.licenseStartDate).split("T")[0] : "",
          endDate: p.licenseEndDate ? String(p.licenseEndDate).split("T")[0] : "",
        };
        return acc;
      }, {}),
    };

    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return baseState;

      const parsed = JSON.parse(saved) || {};
      const mergedProductAccess = {
        ...baseState.productAccess,
        ...(parsed.productAccess || {}),
      };

      return {
        hasPaid: typeof parsed.hasPaid === "boolean" ? parsed.hasPaid : baseState.hasPaid,
        productAccess: mergedProductAccess,
      };
    } catch {
      return baseState;
    }
  };

  const [editData, setEditData] = useState(buildInitialState);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ✅ NEW: اختيار منتج لإضافته
  const [newProductId, setNewProductId] = useState("");

  // ✅ Map للمنتجات العامة (allProducts) علشان نجيب الاسم/الصورة لأي ProductId
  const allProductsMap = useMemo(() => {
    const m = new Map();
    for (const p of allProducts || []) {
      const id = toNumberId(p);
      if (!Number.isNaN(id)) m.set(id, p);
    }
    return m;
  }, [allProducts]);

  const companyProductIds = useMemo(() => {
    return new Set((company.products || []).map((p) => Number(p.productId)));
  }, [company.products]);

  // ✅ المنتجات المتاحة للإضافة = اللي مش موجودة عند الشركة ومش موجودة بالفعل في editData.productAccess
  const availableToAdd = useMemo(() => {
    const access = editData?.productAccess || {};
    return (allProducts || [])
      .map((p) => ({ ...p, _id: toNumberId(p) }))
      .filter((p) => !Number.isNaN(p._id))
      .filter((p) => !companyProductIds.has(p._id))
      .filter((p) => access[p._id] == null);
  }, [allProducts, companyProductIds, editData?.productAccess]);

  // ✅ قائمة الاشتراكات المعروضة = company.products + أي منتجات جديدة اتضافت في editData.productAccess
  const subscriptionProducts = useMemo(() => {
    const list = [...(company.products || [])];

    const accessIds = Object.keys(editData?.productAccess || {}).map((k) => Number(k));
    for (const id of accessIds) {
      if (companyProductIds.has(id)) continue; // موجود بالفعل
      const meta = allProductsMap.get(id);
      // لو المنتج مش موجود في allProducts لأي سبب، برضه نعرضه باسم افتراضي
      list.push({
        productId: id,
        productName: meta?.productName || meta?.name || `Product #${id}`,
        productImage: meta?.productImage || meta?.image || null,
      });
    }

    return list;
  }, [company.products, editData?.productAccess, companyProductIds, allProductsMap]);

  const handleAddProduct = () => {
    const pid = Number(newProductId);
    if (!pid || Number.isNaN(pid)) return;

    setEditData((prev) => ({
      ...prev,
      productAccess: {
        ...(prev.productAccess || {}),
        [pid]: {
          // defaults للمنتج الجديد
          hasPaid: false,
          hasAccess: true,
          startDate: "",
          endDate: "",
        },
      },
    }));

    setNewProductId("");
  };

  const handleDelete = async () => {
    if (!window.confirm(t("admin.companyDetails.deleteConfirm"))) return;
    setDeleting(true);
    await onDelete?.(company.id);
    setDeleting(false);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      localStorage.setItem(storageKey, JSON.stringify(editData));
    } catch {}

    const ok = await onUpdate(company.id, editData); // ✅ لازم onUpdate تعمل assign لكل entries
    setSaving(false);

    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="w-full max-w-5xl bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden my-10">
        <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">{t("admin.companyDetails.title")}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <XCircle className="w-7 h-7" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto text-white">
          {/* Info */}
          <div className="mb-6 p-5 bg-white/10 border border-white/20 rounded-xl shadow-inner backdrop-blur-xl">
            <h3 className="font-bold text-lg text-pink-300 mb-4">{t("admin.companyDetails.infoTitle")}</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-200">{t("admin.companyDetails.companyName")}</p>
                <p className="font-semibold">{company.companyName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-200">{t("admin.companyDetails.companyNumber")}</p>
                <p className="font-semibold">{company.companyNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-200">{t("admin.companyDetails.managerName")}</p>
                <p className="font-semibold">{company.managerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-200">{t("admin.companyDetails.phone")}</p>
                <p className="font-semibold">{company.phoneNumber}</p>
              </div>

              <div className="md:col-span-2">
                <p className="text-sm text-gray-200">{t("admin.companyDetails.email")}</p>
                <p className="font-semibold">{company.email}</p>
              </div>

              {company.address && (
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-200">{t("admin.companyDetails.address")}</p>
                  <p className="font-semibold">{company.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          <div className="mb-6 p-5 bg-white/10 border border-white/20 rounded-xl backdrop-blur-xl">
            <h3 className="font-bold text-lg text-purple-300 mb-4">
              {t("admin.companyDetails.documentsTitle")}
            </h3>

            <div className="grid md:grid-cols-2 gap-3">
              {company.commercialRegistrationPath && (
                <a
                  href={`${IMAGE_BASE}${company.commercialRegistrationPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition"
                >
                  <FileText className="w-5 h-5 text-blue-300 ml-2" />
                  <span className="text-sm">{t("admin.companyDetails.documents.commercialRegistration")}</span>
                </a>
              )}

              {company.taxNumberPath && (
                <a
                  href={`${IMAGE_BASE}${company.taxNumberPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition"
                >
                  <Shield className="w-5 h-5 text-green-300 ml-2" />
                  <span className="text-sm">{t("admin.companyDetails.documents.taxNumber")}</span>
                </a>
              )}

              {company.nationalAddressPath && (
                <a
                  href={`${IMAGE_BASE}${company.nationalAddressPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition"
                >
                  <MapPin className="w-5 h-5 text-purple-300 ml-2" />
                  <span className="text-sm">{t("admin.companyDetails.documents.nationalAddress")}</span>
                </a>
              )}

              {company.logoPath && (
                <a
                  href={`${IMAGE_BASE}${company.logoPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition"
                >
                  <ImageIcon className="w-5 h-5 text-orange-300 ml-2" />
                  <span className="text-sm">{t("admin.companyDetails.documents.logo")}</span>
                </a>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="mb-6 p-5 bg-white/10 border border-white/20 rounded-xl backdrop-blur-xl">
            <h3 className="font-bold text-lg text-green-300 mb-3">
              {t("admin.companyDetails.paymentTitle")}
            </h3>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!editData.hasPaid}
                onChange={(e) => setEditData({ ...editData, hasPaid: e.target.checked })}
                className="w-5 h-5 ml-2"
              />
              <span className="font-medium">{t("admin.companyDetails.paidLabel")}</span>
            </label>
          </div>

          {/* ✅ NEW: Add Product */}
          <div className="mb-6 p-5 bg-white/10 border border-white/20 rounded-xl backdrop-blur-xl">
            <h3 className="font-bold text-lg text-cyan-200 mb-3">
              {t("admin.companyDetails.addProductTitle", "Assign new product")}
            </h3>

            {availableToAdd.length === 0 ? (
              <p className="text-sm text-gray-200">
                {t("admin.companyDetails.noAvailableProducts", "No more products to assign.")}
              </p>
            ) : (
              <div className="flex flex-col md:flex-row gap-3">
              <select
  value={newProductId}
  onChange={(e) => setNewProductId(e.target.value)}
  className="
    flex-1 px-3 py-2 rounded-lg
    bg-black/40 text-white
    border border-white/20
    focus:outline-none focus:ring-2 focus:ring-pink-500/60
  "
>
  <option value="" disabled className="bg-[#0b0b14] text-gray-300">
    {t("admin.companyDetails.selectProductPlaceholder", "Select a product")}
  </option>

  {availableToAdd.map((p) => (
    <option
      key={p._id}
      value={p._id}
      className="bg-[#0b0b14] text-white"
    >
      {p.productName || p.name || `Product #${p._id}`}
    </option>
  ))}
</select>


                <button
                  type="button"
                  onClick={handleAddProduct}
                  disabled={!newProductId}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white
                             flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.companyDetails.addProductBtn", "Add")}
                </button>
              </div>
            )}
          </div>

          {/* Product Subscriptions */}
          <div className="mb-6 p-5 bg-white/10 border border-white/20 rounded-xl backdrop-blur-xl">
            <h3 className="font-bold text-lg text-pink-300 mb-3">
              {t("admin.companyDetails.productSubscriptionsTitle")}
            </h3>

            <div className="space-y-4">
              {subscriptionProducts.map((product) => {
                const pid = Number(product.productId);
                const pa = editData.productAccess?.[pid] || {};
                const meta = allProductsMap.get(pid);

                const name = product.productName || meta?.productName || meta?.name || `Product #${pid}`;
                const img = product.productImage || meta?.productImage || meta?.image || null;

                return (
                  <div key={pid} className="p-4 bg-white/5 border border-white/20 rounded-xl">
                    <div className="flex items-center mb-4">
                      {img ? (
                        <img
                          src={`${IMAGE_BASE}${img}`}
                          alt={name}
                          className="w-10 h-10 rounded ml-2 object-cover"
                        />
                      ) : (
                        <Package className="w-8 h-8 text-purple-300 ml-2" />
                      )}
                      <span className="font-semibold text-white">{name}</span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!pa.hasPaid}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              productAccess: {
                                ...editData.productAccess,
                                [pid]: {
                                  ...pa,
                                  hasPaid: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 ml-2"
                        />
                        <span>{t("admin.companyDetails.paidLabel")}</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!pa.hasAccess}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              productAccess: {
                                ...editData.productAccess,
                                [pid]: {
                                  ...pa,
                                  hasAccess: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 ml-2"
                        />
                        <span>{t("admin.companyDetails.accessLabel")}</span>
                      </label>
                    </div>

                    {!!pa.hasAccess && (
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs mb-1 text-gray-200">
                            {t("admin.companyDetails.startDate")}
                          </label>
                          <input
                            type="date"
                            value={pa.startDate || ""}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                productAccess: {
                                  ...editData.productAccess,
                                  [pid]: {
                                    ...pa,
                                    startDate: e.target.value,
                                  },
                                },
                              })
                            }
                            className="w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-xs mb-1 text-gray-200">
                            {t("admin.companyDetails.endDate")}
                          </label>
                          <input
                            type="date"
                            value={pa.endDate || ""}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                productAccess: {
                                  ...editData.productAccess,
                                  [pid]: {
                                    ...pa,
                                    endDate: e.target.value,
                                  },
                                },
                              })
                            }
                            className="w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={handleSave}
              disabled={loading || saving || deleting}
              className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40"
            >
              {loading || saving ? t("admin.companyDetails.saving") : t("admin.companyDetails.saveChanges")}
            </button>

            <button
              onClick={onClose}
              disabled={deleting}
              className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
            >
              {t("common.cancel")}
            </button>
          </div>

          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <button
                onClick={handleDelete}
                disabled={deleting || saving}
                className="w-full py-3 rounded-xl font-semibold text-white bg-red-700/80 hover:bg-red-700 border border-red-500/40
                           flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <Trash2 className="w-5 h-5" />
                {deleting ? t("admin.companyDetails.deleting") : t("admin.companyDetails.deleteCompany")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsModal;
